/* eslint-disable @typescript-eslint/no-explicit-any */

// 日志模式常量
export const LOG_MODES = {
    DEBUG: 'DEBUG',
    NORMAL: 'NORMAL',
} as const;

// 日志配置接口
interface LoggerOptions {
    filename?: string;
    logMode?: keyof typeof LOG_MODES;
    remoteEndpoint?: string; // 新增：远程日志端点
}

// 日志级别定义和映射
type LogLevel = 'error' | 'warn' | 'info' | 'debug';

// 根据日志模式获取允许的日志级别 - 与服务端保持一致
function getAllowedLevelsForMode(mode: keyof typeof LOG_MODES): LogLevel[] {
    switch (mode) {
        case LOG_MODES.DEBUG:
            // DEBUG模式: 输出 error, warn, info, debug
            return ['error', 'warn', 'info', 'debug'];
        case LOG_MODES.NORMAL:
            // NORMAL模式: 只输出 error
            return ['error'];
        default:
            return ['error'];
    }
}

// 检查日志级别是否允许 - 与服务端保持一致
function isLevelAllowed(
    level: LogLevel,
    mode: keyof typeof LOG_MODES,
): boolean {
    const allowedLevels = getAllowedLevelsForMode(mode);
    return allowedLevels.includes(level);
}

// 格式化日期为与服务端一致的格式
function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// 安全序列化Error对象的工具函数
function safeStringifyError(data: any): string {
    try {
        // 如果是Error对象，手动提取关键属性（避免重复name）
        if (data instanceof Error) {
            // 步骤1：先解构出Error的所有属性，排除name/message/stack（避免重复）
            const { name, message, stack, ...rest } = data;
            // 步骤2：手动定义核心属性，再合并剩余属性（无重复）
            return JSON.stringify({
                name,
                message,
                stack,
                ...rest, // 只合并除了name/message/stack之外的自定义属性
            });
        }
        // 如果是数组（logger.error的多参数场景），递归处理每个元素
        if (Array.isArray(data)) {
            return JSON.stringify(
                data.map(item => {
                    if (item instanceof Error) {
                        const { name, message, stack, ...rest } = item;
                        return {
                            name,
                            message,
                            stack,
                            ...rest,
                        };
                    }
                    return item;
                }),
            );
        }
        // 普通对象直接序列化
        return JSON.stringify(data);
    } catch (e) {
        // 兜底：序列化失败时返回字符串描述
        return String(data);
    }
}

// 上报日志到服务端
async function sendLogToServer(
    level: LogLevel,
    message: any,
    filename: string,
    metadata?: any,
    endpoint?: string,
) {
    try {
        // 检查是否在浏览器环境中
        const isBrowser =
            typeof window !== 'undefined' && typeof navigator !== 'undefined';

        // 如果不在浏览器环境中，直接返回，不进行日志上报
        if (!isBrowser) {
            return;
        }

        // 使用提供的端点或默认端点
        const logEndpoint = endpoint || '/api/logs';

        // 使用安全序列化函数处理message
        const serializedMessage = safeStringifyError(message);

        // metadata也做安全处理
        safeStringifyError(metadata || {});

        // 构建日志数据
        const logData = {
            level,
            message: serializedMessage,
            filename,
            timestamp: formatDate(new Date()),
            metadata: metadata || {},
            userAgent: navigator?.userAgent || 'unknown',
            url: window?.location?.href || 'unknown',
        };

        const logStr = (() => {
            try {
                return JSON.stringify(logData);
            } catch {
                return '{}';
            }
        })();

        // 发送日志请求（使用navigator.sendBeacon或不阻塞的fetch）
        if (navigator.sendBeacon) {
            // 使用sendBeacon，在页面卸载时也能可靠发送
            navigator.sendBeacon(logEndpoint, logStr);
        } else {
            // 使用fetch，不等待响应
            fetch(logEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: logStr,
                keepalive: true, // 确保请求在页面关闭后也能完成
            }).catch(err => {
                console.warn('日志上报失败:', err);
            });
        }
    } catch (error) {
        // 静默失败，不影响主流程
        console.warn('日志上报异常:', error);
    }
}

// 客户端创建日志器 - 与服务端保持一致的接口
export function createClientLogger(options: LoggerOptions | string = {}) {
    // 处理参数，支持字符串或对象格式
    const config =
        typeof options === 'string' ? { filename: options } : options;
    const filename = config.filename || 'app';

    // 获取日志模式配置，默认为NORMAL - 与服务端保持一致
    const logMode =
        (process.env.NEXT_PUBLIC_LOG_MODE as keyof typeof LOG_MODES) ||
        config.logMode ||
        LOG_MODES.NORMAL;

    // 获取环境信息
    // const isProduction = process.env.NODE_ENV === 'production';

    // 配置远程日志
    const remoteEndpoint = config.remoteEndpoint || '/api/logs';

    // 客户端的控制台日志实现
    const clientLogger = {
        debug: (...args: any[]) => {
            // DEBUG模式下输出debug级别
            if (logMode === LOG_MODES.DEBUG) {
                console.log(`[DEBUG] ${filename}:`, ...args);
                sendLogToServer('debug', args, filename, {}, remoteEndpoint);
            }
        },
        info: (...args: any[]) => {
            // DEBUG模式下输出info - 与服务端保持一致
            if (isLevelAllowed('info', logMode)) {
                console.log(`[INFO] ${filename}:`, ...args);
                sendLogToServer('info', args, filename, {}, remoteEndpoint);
            }
        },
        warn: (...args: any[]) => {
            // DEBUG模式下输出warn - 与服务端保持一致
            if (isLevelAllowed('warn', logMode)) {
                console.warn(`[WARN] ${filename}:`, ...args);
                sendLogToServer('warn', args, filename, {}, remoteEndpoint);
            }
        },
        error: (...args: any[]) => {
            // 所有模式下都输出error - 与服务端保持一致
            if (isLevelAllowed('error', logMode)) {
                console.error(`[ERROR] ${filename}:`, ...args);
                // 远程记录（error级别总是记录）
                sendLogToServer('error', args, filename, {}, remoteEndpoint);
            }
        },

        // 新增：直接记录日志的方法
        log: (level: LogLevel, message: any, metadata?: any) => {
            if (!isLevelAllowed(level, logMode)) return;

            const consoleMethod = {
                error: console.error,
                warn: console.warn,
                info: console.log,
                debug: console.debug,
            }[level];

            consoleMethod?.(`[${level.toUpperCase()}] ${filename}:`, message);
            sendLogToServer(level, message, filename, metadata, remoteEndpoint);
        },

        transports: [], // 为了保持接口一致，添加空的transports数组
    };

    return clientLogger;
}

// 批量上报日志，避免频繁请求
export class BatchLogger {
    private logs: Array<any> = [];
    private batchSize = 10;
    private flushInterval = 5000; // 5秒
    private timer: NodeJS.Timeout | null = null;
    private endpoint: string;

    constructor(endpoint = '/api/logs/batch') {
        this.endpoint = endpoint;
        this.startBatchTimer();

        // 页面卸载时确保发送所有日志
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => this.flushAll());
        }
    }

    addLog(log: any) {
        // 确保日志对象包含与服务端一致的时间戳格式
        if (!log.timestamp) {
            log.timestamp = formatDate(new Date());
        }
        this.logs.push(log);

        // 达到批量大小立即发送
        if (this.logs.length >= this.batchSize) {
            this.flush();
        }
    }

    private flush() {
        try {
            if (this.logs.length === 0) return;

            // 检查是否在浏览器环境中
            const isBrowser =
                typeof window !== 'undefined' &&
                typeof navigator !== 'undefined';
            if (!isBrowser) {
                this.logs = []; // 清空日志但不发送
                return;
            }

            const logsToSend = [...this.logs];
            this.logs = [];

            const logStr = (() => {
                try {
                    return JSON.stringify(logsToSend);
                } catch {
                    return '[]';
                }
            })();

            if (navigator.sendBeacon) {
                navigator.sendBeacon(this.endpoint, logStr);
            } else {
                fetch(this.endpoint, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: logStr,
                    keepalive: true,
                }).catch(console.error);
            }
        } catch {
            /* empty */
        }
    }

    private flushAll() {
        this.flush();
        if (this.timer) {
            clearInterval(this.timer);
        }
    }

    private startBatchTimer() {
        this.timer = setInterval(() => {
            if (this.logs.length > 0) {
                this.flush();
            }
        }, this.flushInterval);
    }
}

// 创建默认logger，从环境变量获取日志模式 - 与服务端保持一致
export const logger = createClientLogger({
    logMode: process.env.NEXT_PUBLIC_LOG_MODE as keyof typeof LOG_MODES,
});

// React Hook for using logger in components
export function useLogger(componentName: string) {
    // 为组件创建专用的日志器实例 - 返回与服务端一致的接口
    return createClientLogger({
        filename: componentName,
    });
}
